<template>
  <v-dialog
    v-model="isVisible"
    persistent
    scrollable
  >
    <v-card ref="legalCaseEditorNewPartyDialogCard">
      <v-toolbar
        color="secondary"
        dark
        tile
      >
        <v-toolbar-title>
          Add a new party
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          icon
          @click="_ => onCloseDialog()"
        >
          <v-icon>
            mdi-close
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-5">
        <v-row>
          <v-col>
            <PartyFields
              :party="party"
              required
              @update="party => updateParty(party)"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn
          text
          @click="_ => onCloseDialog()"
        >
          Cancel
        </v-btn>

        <v-spacer />

        <v-btn
          :disabled="!isValid"
          text
          @click="_ => onCreateParty()"
        >
          Add
        </v-btn>
      </v-card-actions>
    </v-card>

    <PEPartyDuplicatesDialog
      v-model="showDuplicatesDialog"
      :duplicates="duplicates"
      :on-proceed="_ => onDuplicateResolved()"
    />
  </v-dialog>
</template>

<script>
import { isEmpty } from 'lodash'
import { mapActions } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { filterValidParties } from '@/utilities/party'

import { PARTY_NAMESPACE } from '@/store/party'

import PEPartyDuplicatesDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyDuplicatesDialog.vue'
import PartyFields from './LegalCaseEditorPartyFields.vue'

export default {
  name: 'LegalCaseEditorPartyFieldsDialog',

  components: {
    PEPartyDuplicatesDialog,
    PartyFields,
  },

  model: {
    prop: 'visible',
    event: 'change',
  },

  props: {
    party: {
      default: _ => ({}),
      type: Object,
    },

    visible: {
      required: true,
      type: Boolean,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      duplicates: [],
      editedParty: {},
      showDuplicatesDialog: false,
    }
  },

  computed: {
    isValid () {
      return this.editedParty && !isEmpty(filterValidParties([this.editedParty])) && !this.v$.$invalid
    },

    isVisible: {
      get () { return this.visible },
      set (value) { this.$emit('change', value === true) },
    },
  },

  methods: {
    onCloseDialog () {
      this.isVisible = false
    },

    async onCreateParty () {
      this.duplicates = await this.detectDuplicate({
        first_name: this.editedParty.first_name,
        last_name: this.editedParty.last_name,
      })

      if (this.duplicates.length > 0) this.showDuplicatesDialog = true
      else this.onDuplicateResolved()
    },

    onDuplicateResolved () {
      if (!this.isValid) return
      this.showDuplicatesDialog = false

      this.save()
    },

    partyHasEmail () {
      return this.editedParty.email?.no_email || this.editedParty.email?.address
    },

    partyHasPhone () {
      return this.editedParty.phone?.phone_number
    },

    save () {
      this.$emit('save', this.editedParty)
      this.onCloseDialog()
    },

    updateParty (party) {
      if (!party.email?.no_email && !isEmpty(party.email?.address)) party.email.primary = true

      if (!isEmpty(party.phone?.phone_number)) {
        // TODO: Check if there is some guard clause to add as the user can select
        // amongst all know phone types, however there are some types that are not
        // compatible with the current workflow and requirements, e.g. 'Voice only'
        // breaks the intent of sending something via text to the party.
        party.phone.preferred_text = true
        party.phone.preferred_voice = true
      }

      this.editedParty = {
        ...party,
        primary_email: party.email,
        primary_text_number: party.phone,
      }
    },

    ...mapActions(PARTY_NAMESPACE, [
      'detectDuplicate',
    ]),
  },
}
</script>
